import { BetSelection, EventPeriod, EventType, MarketGroup, MarketType, TradingType } from '@/constants';
import { AbstractModel } from '@/models/abstractModel';
import { betSelectionDictionary } from '@/stores/simpleDictionary/betSelection';
import { currencyDictionary } from '@/stores/simpleDictionary/currency';
import { deviceTypeDictionary } from '@/stores/simpleDictionary/deviceType';
import { platformDictionary } from '@/stores/simpleDictionary/platform';
import { IBetDataAPI } from '@/types';

export abstract class AbstractBet extends AbstractModel {
    public accountCurrencyId: number;
    public accountId: number;
    public accountLogin: string;
    public accountValidPerc: number;
    public awayWinPercClose: string | null;
    public betId: number;
    public betInternalId: string;
    public bookmakerId: number;
    public botDeletedAt: string | null;
    public botId: number | null;
    public botName: string | null;
    public cornersAway: number | null;
    public cornersAwayFt: number | null;
    public cornersAwayHt: number | null;
    public cornersHome: number | null;
    public cornersHomeFt: number | null;
    public cornersHomeHt: number | null;
    public correctScore: string | null;
    public createdAt: string;
    public customEventId: string;
    public customLeagueId: number;
    public customLeagueName: string;
    public customTeamAwayId: number | null;
    public customTeamAwayName: string;
    public customTeamHomeId: number | null;
    public customTeamHomeName: string;
    public dateGame: string;
    public deviceTypeId: number | null;
    public eventId: number;
    public eventType: EventType;
    public exchangeRate: string;
    public feedsCount: number;
    public handicap: string | null;
    public hdpVMainClose: string | null;
    public hdpVMainOpen: string | null;
    public homeWinPercClose: string | null;
    public id: string;
    public isNvenue: number;
    public iso3: string;
    public isStatusBookmakerFixedManually: 0 | 1;
    public isStatusFixedManually: 0 | 1;
    public jollywhere: number | null;
    public jollywhereClose: number | null;
    public leagueId: number | null;
    public leagueName: string;
    public marginPerc: string;
    public marketTypeId: MarketType;
    public odds: string; // player odds
    public oddsClose: string | null;
    public oddsExtraFee: string | undefined; // additional commission to odds in %
    public oddsFee: string; // commission to odds in %
    public oddsOpen: string | null;
    public orderId: number | null;
    public orderInternalId: number | null;
    public ouVMainClose: string | null;
    public ouVMainOpen: string | null;
    public periodTypeId: number;
    public platformId: number;
    public score: string | null;
    public scoreAway: number | null;
    public scoreFt: string | null;
    public scoreHome: number | null;
    public scoreHt: string | null;
    public selectionId: BetSelection;
    public situation: string;
    public stakePc: string; // player stake
    public teamAwayId: number | null;
    public teamAwayName: string;
    public teamHomeId: number | null;
    public teamHomeName: string;
    public teamwhere: number | null;
    public time: string | null;
    public timeH: number | null;
    public timeMin: number | null;
    public topup: string | null;
    public tradingTypeId: number;
    public userCurrencyId: number;
    public userId: number;
    public userLogin: string;
    public wldAwayClose: string | null;
    public wldDrawClose: string | null;
    public wldHomeClose: string | null;

    constructor(data: IBetDataAPI) {
        super();
        this.id = data.id;
        this.createdAt = data.created_at;
        this.dateGame = data.date_game;
        this.userLogin = data.user_login;
        this.userId = data.user_id;
        this.platformId = data.tool_user_platform_id;
        this.deviceTypeId = data.device_type_id;
        this.orderInternalId = data.order_internal_id;
        this.orderId = data.order_id;
        this.bookmakerId = data.site_id;
        this.accountLogin = data.account_login;
        this.accountCurrencyId = data.account_currency_id;
        this.userCurrencyId = data.user_currency_id;
        this.customEventId = data.custom_event_id;
        this.customLeagueId = data.custom_league_id;
        this.customLeagueName = data.custom_league_name;
        this.leagueName = data.league_name;
        this.customTeamHomeName = data.custom_team_home_name;
        this.customTeamAwayName = data.custom_team_away_name;
        this.teamHomeName = data.team_home_name;
        this.teamAwayName = data.team_away_name;
        this.situation = data.situation;
        this.selectionId = data.selection_id;
        this.handicap = data.handicap;
        this.correctScore = data.correct_score;
        this.teamwhere = data.teamwhere;
        this.jollywhere = data.jollywhere;
        this.jollywhereClose = data.jollywhere_close;
        this.odds = data.odds;
        this.oddsOpen = data.odds_open;
        this.oddsClose = data.odds_close;
        this.oddsFee = data.odds_fee;
        this.oddsExtraFee = data.odds_extra_fee ?? undefined;
        this.stakePc = data.stake_pc;
        this.accountValidPerc = data.account_valid_perc;
        this.score = data.score;
        this.scoreHt = data.score_ht;
        this.scoreFt = data.score_ft;
        this.periodTypeId = data.period_type_id;
        this.eventType = data.is_live === 1 ? EventType.Live : EventType.Pregame;
        this.isNvenue = data.is_nvenue;
        this.time = data.time;
        this.timeH = data.time_h;
        this.timeMin = data.time_min;
        this.botId = data.bot_id;
        this.botName = data.bot_name;
        this.botDeletedAt = data.bot_deleted_at;
        this.exchangeRate = data.exchange_rate;
        this.iso3 = data.iso3;
        this.feedsCount = data.feeds_count;
        this.marginPerc = data.margin_perc;
        this.homeWinPercClose = data.home_win_perc_close;
        this.awayWinPercClose = data.away_win_perc_close;
        this.wldHomeClose = data.wld_home_close;
        this.wldDrawClose = data.wld_draw_close;
        this.wldAwayClose = data.wld_away_close;
        this.hdpVMainOpen = data.hdp_v_main_open;
        this.hdpVMainClose = data.hdp_v_main_close;
        this.ouVMainOpen = data.ou_v_main_open;
        this.ouVMainClose = data.ou_v_main_close;
        this.cornersHome = data.corners_home;
        this.cornersAway = data.corners_away;
        this.cornersHomeHt = data.corners_home_ht;
        this.cornersAwayHt = data.corners_away_ht;
        this.cornersHomeFt = data.corners_home_ft;
        this.cornersAwayFt = data.corners_away_ft;
        this.customTeamHomeId = data.custom_team_home_id ?? null;
        this.customTeamAwayId = data.custom_team_away_id ?? null;
        this.teamHomeId = data.team_home_id ?? null;
        this.teamAwayId = data.team_away_id ?? null;
        this.leagueId = data.league_id ?? null;
        this.betId = data.bet_id ?? null;
        this.betInternalId = data.bet_internal_id ?? null;
        this.accountId = data.account_id;
        this.marketTypeId = data.market_type_id as MarketType;
        if (data.score) {
            const [home, away] = data.score.split('-');
            this.scoreHome = Number(home);
            this.scoreAway = Number(away);
        } else {
            this.scoreHome = this.scoreAway = null;
        }
        this.eventId = data.event_id;
        this.topup = data.topup;
        this.tradingTypeId = data.trading_type_id;
        this.isStatusFixedManually = data.is_status_fixed_manually ?? 0;
        this.isStatusBookmakerFixedManually = data.is_status_bookmaker_fixed_manually ?? 0;
    }

    get accountLabel(): string {
        const symbol = currencyDictionary.itemById(this.accountCurrencyId).symbol;
        return `${this.accountLogin} (${symbol})`;
    }

    get betDescription(): string {
        const parts: string[] = [];
        parts.push(betSelectionDictionary.getNameWithShortMarket(this.selectionId));

        if (this.correctScore) {
            parts.push(this.correctScore);
        }

        const handicap = this.formatHandicapValue(
            this.handicap,
            [
                BetSelection.HandicapHome,
                BetSelection.HandicapAway,
                BetSelection.CornersHandicapHome,
                BetSelection.CornersHandicapAway,
                BetSelection.SpreadHome,
                BetSelection.SpreadAway,
            ].includes(this.selectionId)
        );
        if (handicap) {
            parts.push(handicap);
        }

        return parts.join(' ').trim();
    }

    get betSelectionTypeLabel(): string {
        return betSelectionDictionary.getName(this.selectionId);
    }

    get botOrderDisplay(): { value: string; tooltip: string; class: string } {
        if (this.tradingTypeId === TradingType.BetOrder) {
            return {
                value: `#${this.orderInternalId}`,
                tooltip: `Order ID: ${this.orderId}`,
                class: '',
            };
        }

        if (this.botDeletedAt) {
            const botName = this.botName?.split('|').slice(0, -1).join('|');
            return {
                value: 'Deleted',
                tooltip: botName!,
                class: 'text-error',
            };
        }

        return {
            value: this.botName!,
            tooltip: `Bot ID: ${this.botId}`,
            class: '',
        };
    }

    get deviceTypeLabel(): string {
        return this.deviceTypeId === null ? '' : deviceTypeDictionary.nameById(this.deviceTypeId);
    }

    get displayedScore(): string {
        if (this.displayedScoreHome === null) {
            return '';
        }
        return `${this.displayedScoreHome}-${this.displayedScoreAway}`;
    }

    get displayedScoreAway(): number | null {
        if (this.eventType === EventType.Pregame) {
            return null;
        }

        return this.marketGroup === MarketGroup.Corners ? this.cornersAway : this.scoreAway;
    }

    get displayedScoreHome(): number | null {
        if (this.eventType === EventType.Pregame) {
            return null;
        }

        return this.marketGroup === MarketGroup.Corners ? this.cornersHome : this.scoreHome;
    }

    // Final/settlement score used to recompute the bet result, chosen by market group and period.
    // Regular markets use goals score (HT/FT), corners markets use corners score (HT/FT).
    get finalScore(): string | null {
        const isFirstHalf = this.periodTypeId === EventPeriod.FirstHalf;

        if (this.marketGroup === MarketGroup.Corners) {
            const home = isFirstHalf ? this.cornersHomeHt : this.cornersHomeFt;
            const away = isFirstHalf ? this.cornersAwayHt : this.cornersAwayFt;
            if (home === null || away === null) return null;
            return `${home}-${away}`;
        }

        const parsed = isFirstHalf ? this.parsedScoreHt : this.parsedScoreFt;
        if (parsed.home === null || parsed.away === null) return null;
        return `${parsed.home}-${parsed.away}`;
    }

    get getBotName() {
        if (!this.botDeletedAt) {
            return this.botName;
        }
        return this.botName?.split('|').slice(0, -1).join('|');
    }

    get hasFinalScore(): boolean {
        return this.finalScore !== null;
    }

    get hasStatusWarning(): boolean {
        return this.isStatusFixedManually === 1 || this.isStatusBookmakerFixedManually === 1;
    }

    get infoTooltip(): string {
        const warnings: string[] = [];

        if (this.isStatusFixedManually === 1) {
            warnings.push(this.t('bets.statusWarning.manuallySetPlayer'));
        }

        if (this.isStatusBookmakerFixedManually === 1) {
            warnings.push(this.t('bets.statusWarning.manuallySetBookmaker'));
        }

        if (warnings.length > 0) {
            return warnings.join('<br>');
        }

        return ''; // Вернется старый тултип из компонента, если пусто
    }

    get marketGroup(): MarketGroup {
        if (this.marketTypeId === MarketType.CornersHandicap || this.marketTypeId === MarketType.CornersGoal) {
            return MarketGroup.Corners;
        }

        return MarketGroup.Regular;
    }

    get parsedScoreFt() {
        return this.parseScore(this.scoreFt);
    }

    get parsedScoreHt() {
        return this.parseScore(this.scoreHt);
    }

    get platformName(): string {
        return platformDictionary.nameById(this.platformId);
    }

    get statusWarningTooltip(): string {
        const warnings: string[] = [];

        if (this.isStatusFixedManually === 1) {
            warnings.push(this.t('bets.statusWarning.manuallySetPlayer'));
        }

        if (this.isStatusBookmakerFixedManually === 1) {
            warnings.push(this.t('bets.statusWarning.manuallySetBookmaker'));
        }

        return warnings.join('<br>');
    }

    get userLabel(): string {
        return this.platformName ? `${this.userLogin} (${this.platformName})` : this.userLogin;
    }

    formatHandicapValue(value: string | null, showPlus = false): string | null {
        if (value === null || value === '') return null;
        const numeric = +value;
        const formatted = numeric === 0 ? numeric.toFixed(0) : numeric.toFixed(2);
        return `${showPlus && numeric > 0 ? '+' : ''}${formatted}`;
    }

    parseScore(score: string | null) {
        if (!score) return { home: null, away: null };
        const parts = score.split('-');
        if (parts.length !== 2) return { home: null, away: null };
        return {
            home: parts[0]?.trim() || null,
            away: parts[1]?.trim() || null,
        };
    }
}
